import React, {
  useEffect,
  useState,
} from 'react';
import {
  Link,
} from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Paper,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Group as GroupIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  SportsEsports as GameIcon,
  Person as PersonIcon,
  Wifi as WifiIcon,
  Hotel as HotelIcon,
  Restaurant as RestaurantIcon,
  CheckCircle as CheckIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import {
  eventsService,
} from '../services/events';
import { useSettings } from '../contexts/SettingsContext';
import { resolveMediaUrl } from '../utils/mediaUrl';

const formatShortDate = (dateString) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${day}.${month}.${year}`;
};

const formatLongDate = (dateString) => {
  return new Date(dateString).toLocaleString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const eventDurationHours = (event) => {
  if (!event?.start_date || !event?.end_date) return null;
  const hours = (new Date(event.end_date) - new Date(event.start_date)) / (1000 * 60 * 60);
  if (!Number.isFinite(hours) || hours <= 0) return null;
  return Math.round(hours);
};

const LandingPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { settings } = useSettings();
  const heroImage = settings.landing_hero_image
    ? resolveMediaUrl(settings.landing_hero_image)
    : '/theme/oc-bar.png';
  const gamingImage = settings.landing_gaming_image
    ? resolveMediaUrl(settings.landing_gaming_image)
    : '/theme/oc-lan-wide.png';
  const barImage = settings.landing_bar_image
    ? resolveMediaUrl(settings.landing_bar_image)
    : '/theme/oc-bar-detail.png';
  const [activeEvent, setActiveEvent] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadActiveEvent = async () => {
      const result = await eventsService.getActiveEvent();
      if (!cancelled && result.success) {
        setActiveEvent(result.data);
      }
    };

    loadActiveEvent();
    return () => {
      cancelled = true;
    };
  }, []);

  const durationHours = eventDurationHours(activeEvent);
  const dateRange = activeEvent
    ? `${formatShortDate(activeEvent.start_date)} - ${formatShortDate(activeEvent.end_date)}`
    : 'Dates coming soon';
  const eventYear = activeEvent ? String(new Date(activeEvent.start_date).getFullYear()) : '';
  const eventTitle = activeEvent?.name || 'Next event';
  const gamerCount = activeEvent ? String(activeEvent.max_participants) : null;

  // Event stats for quick overview
  const eventStats = [
    { icon: <GroupIcon />, value: gamerCount || '—', label: 'Gamers', color: '#C9A227' },
    { icon: <ScheduleIcon />, value: durationHours ? `${durationHours}h+` : '—', label: 'Gaming Marathon', color: '#E85D4C' },
    { icon: <GameIcon />, value: '6+', label: 'Fun Challenges', color: '#6B8F71' },
    { icon: <RestaurantIcon />, value: 'Full Bar', label: 'Drinks & Food', color: '#3D7A8C' },
  ];

  // Feature highlights
  const features = [
    {
      icon: <GameIcon />,
      title: 'Fun Challenges',
      description: 'Try your hand at putting, Assetto Corsa racing, Blobby Volley, Trackmania, CS2 Wingman, and more!',
      color: 'primary',
    },
    {
      icon: <RestaurantIcon />,
      title: 'Bar & Socializing',
      description: 'Enjoy drinks and food at the Old Capitol bar. Half of us are here just to hang out and have fun!',
      color: 'secondary',
    },
    {
      icon: <GroupIcon />,
      title: 'Casual Atmosphere',
      description: 'No pressure, no stress. Come for the games, stay for the vibes and community.',
      color: 'success',
    },
    {
      icon: <LocationIcon />,
      title: 'Perfect Venue',
      description: 'Old Capitol club combines gaming space with a great bar atmosphere for the ultimate hangout.',
      color: 'warning',
    },
    {
      icon: <HotelIcon />,
      title: 'Sleeping Area',
      description: 'Need a rest between sessions? There is a sleeping area at the venue, and three hotels nearby if you want a real bed.',
      color: 'secondary',
    },
    {
      icon: <WifiIcon />,
      title: 'All Setup Ready',
      description: 'High-speed internet, power, tables, chairs - just bring your gear and good vibes!',
      color: 'error',
    },
  ];

  // How it works steps
  const howItWorksSteps = [
    {
      number: '01',
      title: 'Register',
      description: 'Create your account and secure your spot. Only 30 places available!',
      icon: <PersonIcon />,
    },
    {
      number: '02',
      title: 'Pack Your Gear',
      description: 'Bring your PC/laptop, peripherals, and good vibes. We handle the rest!',
      icon: <GameIcon />,
    },
    {
      number: '03',
      title: 'Game & Chill',
      description: 'Try fun challenges, hang at the bar, make friends. It\'s all about having a great time!',
      icon: <RestaurantIcon />,
    },
  ];

  // FAQ items
  const faqItems = [
    {
      question: 'What should I bring?',
      answer: 'Bring your gaming PC/laptop, peripherals (mouse, keyboard, headset), and good vibes! We provide tables, chairs, power, and internet.',
    },
    {
      question: 'Is this a competitive event?',
      answer: 'Not at all! We have fun challenges like putting, Assetto Corsa racing, Blobby Volley, Trackmania, and CS2 Wingman, but it\'s all casual. Half of us are just here to socialize and enjoy the bar!',
    },
    {
      question: 'Can I just come to hang out?',
      answer: 'Absolutely! You don\'t need to participate in challenges. Come for the community, drinks, and good times!',
    },
    {
      question: 'Can I sleep there?',
      answer: 'Yes. There is a sleeping area at the venue. If you would rather stay in a hotel, three are nearby: Hotel Meilenstein, Parkhotel Langenthal, and the Auberge.',
    },
    {
      question: 'When exactly does it run?',
      answer: activeEvent
        ? `${formatLongDate(activeEvent.start_date)} until ${formatLongDate(activeEvent.end_date)}.${durationHours ? ` That's about ${durationHours} hours of gaming!` : ''} Come and go as you please.`
        : 'Dates will be announced once the next event is set.',
    },
  ];

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: '100vh', sm: '600px', md: '700px' },
          display: 'flex',
          alignItems: 'center',
          backgroundImage: `url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: { xs: 'scroll', md: 'fixed' }, // Fixed causes performance issues on mobile
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(20, 17, 14, 0.88) 0%, rgba(36, 30, 24, 0.85) 100%)',
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'transparent',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: 'center', color: 'white', py: { xs: 4, sm: 0 } }}>
            {/* Location Badge */}
            <Chip
              icon={<LocationIcon />}
              label="Old Capitol Club • Langenthal"
              sx={{
                mb: { xs: 2, sm: 3 },
                bgcolor: alpha('#C9A227', 0.2),
                color: 'white',
                border: '1px solid rgba(201, 162, 39, 0.3)',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                py: { xs: 2, sm: 2.5 },
                px: { xs: 0.5, sm: 1 },
                '& .MuiChip-icon': { color: '#E4C56A' },
              }}
            />

            {/* Main Title */}
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', sm: '4rem', md: '5.5rem' },
                fontWeight: 800,
                mb: { xs: 1.5, sm: 2 },
                color: '#F3EDE2', fontFamily: '"Fraunces", serif',
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
              }}
            >
              {eventTitle}
            </Typography>

            {eventYear && (
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '1.25rem', sm: '2rem', md: '2.5rem' },
                fontWeight: 600,
                mb: { xs: 0.5, sm: 1 },
                color: '#C9A227',
                fontFamily: '"Barlow Condensed", sans-serif',
              }}
            >
              {eventYear}
            </Typography>
            )}

            {/* Subtitle */}
            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: '1rem', sm: '1.2rem', md: '1.5rem' },
                fontWeight: 400,
                mb: { xs: 1.5, sm: 2 },
                color: alpha('#fff', 0.9),
                maxWidth: '800px',
                mx: 'auto',
                px: { xs: 2, sm: 0 },
              }}
            >
              {activeEvent
                ? `${durationHours ? `${durationHours}h+` : ''} Gaming Marathon. ${gamerCount} gamers. Endless fun.`
                : 'Dates coming soon. Endless fun.'}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '0.875rem', sm: '1rem', md: '1.1rem' },
                mb: { xs: 1, sm: 1 },
                color: '#C9A227',
                fontFamily: '"Barlow Condensed", sans-serif',
                fontWeight: 600,
                maxWidth: '700px',
                mx: 'auto',
              }}
            >
              {dateRange}
            </Typography>


            {/* CTA Button */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: { xs: 4, sm: 6 },
                mt: { xs: 3, sm: 0 },
                px: { xs: 2, sm: 0 },
              }}
            >
              <Button
                component={Link}
                to="/login"
                variant="contained"
                size="large"
                endIcon={<ArrowIcon />}
                fullWidth={isMobile}
                sx={{
                  px: { xs: 4, sm: 6 },
                  py: { xs: 2, sm: 2.5 },
                  fontSize: { xs: '1rem', sm: '1.2rem' },
                  backgroundColor: '#E85D4C',
                  color: '#14110E',
                  fontWeight: 700,
                  border: '1px solid rgba(201, 162, 39, 0.3)',
                  maxWidth: { xs: '100%', sm: '400px' },
                  '&:hover': {
                    backgroundColor: '#FFFFFF',
                    color: '#14110E',
                  },
                }}
              >
                Register Now
              </Button>
            </Box>

            {/* Event Stats */}
            <Grid container spacing={{ xs: 1.5, sm: 2 }} justifyContent="center">
              {eventStats.map((stat, index) => (
                <Grid item xs={6} sm={3} key={index}>
                  <Paper
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      bgcolor: alpha('#241E18', 0.85),
                      border: `1px solid ${alpha('#C9A227', 0.45)}`,
                      textAlign: 'center',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: '#C9A227',
                      },
                    }}
                  >
                    <Box sx={{ color: stat.color, mb: { xs: 0.5, sm: 1 } }}>
                      {React.cloneElement(stat.icon, { sx: { fontSize: { xs: 24, sm: 32 } } })}
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" sx={{ color: alpha('#fff', 0.7), fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {stat.label}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Venue Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, sm: 8, md: 12 }, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 6 } }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' } }}>
            The Venue
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '700px', mx: 'auto', fontSize: { xs: '1rem', sm: '1.25rem' }, px: { xs: 2, sm: 0 } }}>
            Old Capitol Club in Langenthal provides the perfect setting for an unforgettable LAN experience
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Venue Image 1 - Gaming Area */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                height: { xs: 200, sm: 250, md: 300 },
                borderRadius: 2,
                overflow: 'hidden',
                backgroundImage: `url(${gamingImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                position: 'relative',
                boxShadow: 3,
                transition: 'transform 0.3s ease',
                '&:hover': {
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '40%',
                  background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)',
                },
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  bottom: { xs: 12, sm: 16 },
                  left: { xs: 12, sm: 16 },
                  zIndex: 1,
                  color: 'white',
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 600, textShadow: '0 2px 4px rgba(0,0,0,0.8)', fontSize: { xs: '1.125rem', sm: '1.5rem' } }}>
                  Gaming Area
                </Typography>
                <Typography variant="body2" sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  Premium Setup
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Venue Image 2 - Bar Area */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                height: { xs: 200, sm: 250, md: 300 },
                borderRadius: 2,
                overflow: 'hidden',
                backgroundImage: `url(${barImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                position: 'relative',
                boxShadow: 3,
                transition: 'transform 0.3s ease',
                '&:hover': {
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '40%',
                  background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)',
                },
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  bottom: { xs: 12, sm: 16 },
                  left: { xs: 12, sm: 16 },
                  zIndex: 1,
                  color: 'white',
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 600, textShadow: '0 2px 4px rgba(0,0,0,0.8)', fontSize: { xs: '1.125rem', sm: '1.5rem' } }}>
                  Bar & Lounge
                </Typography>
                <Typography variant="body2" sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  Relax & Recharge
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Venue Details */}
        <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, mt: { xs: 2, sm: 3, md: 4 } }}>
          <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                Why Old Capitol Club?
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                Located in the heart of Langenthal, Old Capitol Club offers the perfect blend of gaming
                and socializing. The venue features high-speed internet, spacious gaming areas, and most
                importantly - a full bar where you can grab drinks and hang out between gaming sessions.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                With its central location and excellent public transport, it's the ideal spot for a 
                72+ hour gaming marathon where the focus is on fun, not competition.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                href="https://oldcapitol.ch/"
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                size="large"
                fullWidth
                endIcon={<ArrowIcon />}
                sx={{ py: { xs: 1.5, sm: 2 } }}
              >
                Visit Old Capitol Club
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      {/* Features Section */}
      <Box sx={{ bgcolor: 'background.default', py: { xs: 6, sm: 8, md: 12 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 6 } }}>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' } }}>
              What to Expect
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '700px', mx: 'auto', fontSize: { xs: '1rem', sm: '1.25rem' }, px: { xs: 2, sm: 0 } }}>
              A chill gaming hangout with great people and good times
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    '&:hover': {
                      borderColor: 'secondary.main',
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box
                      sx={{
                        width: { xs: 48, sm: 56 },
                        height: { xs: 48, sm: 56 },
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: alpha(theme.palette[feature.color].main, 0.1),
                        color: `${feature.color}.main`,
                        mb: 2,
                      }}
                    >
                      {React.cloneElement(feature.icon, { sx: { fontSize: { xs: 28, sm: 32 } } })}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, sm: 8, md: 12 }, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 6 } }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' } }}>
            How It Works
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '700px', mx: 'auto', fontSize: { xs: '1rem', sm: '1.25rem' }, px: { xs: 2, sm: 0 } }}>
            Three simple steps to join the action
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 3, sm: 4 }}>
          {howItWorksSteps.map((step, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    width: { xs: 80, sm: 100 },
                    height: { xs: 80, sm: 100 },
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    mx: 'auto',
                    mb: { xs: 2, sm: 3 },
                    position: 'relative',
                  }}
                >
                  {React.cloneElement(step.icon, { sx: { fontSize: { xs: 40, sm: 48 } } })}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: { xs: -8, sm: -10 },
                      right: { xs: -8, sm: -10 },
                      width: { xs: 32, sm: 40 },
                      height: { xs: 32, sm: 40 },
                      borderRadius: '50%',
                      bgcolor: 'secondary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: { xs: '1rem', sm: '1.2rem' },
                    }}
                  >
                    {step.number}
                  </Box>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                  {step.title}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, px: { xs: 2, sm: 0 } }}>
                  {step.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* FAQ Section */}
      <Box sx={{ bgcolor: 'background.default', py: { xs: 6, sm: 8, md: 12 } }}>
        <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 6 } }}>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' } }}>
              Frequently Asked Questions
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Got questions? We've got answers.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {faqItems.map((faq, index) => (
              <Paper key={index} sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  <CheckIcon sx={{ color: 'success.main', fontSize: { xs: 20, sm: 24 }, flexShrink: 0, mt: 0.5 }} />
                  {faq.question}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ pl: { xs: 3.5, sm: 4 }, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {faq.answer}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;

